const API_BASE_URL = 'https://api.realitni-jestrab.cz'

// Token storage key (same as authService)
const ACCESS_TOKEN_KEY = 'realitni_access_token'

export const valuationService = {
  // Get a valuation through its revocable public share token
  async getPublicValuation(shareToken) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/public/${encodeURIComponent(shareToken)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const error = new Error(response.status === 404 ? 'Sdílený odhad neexistuje nebo byl zrušen' : `HTTP error! status: ${response.status}`)
      error.response = response
      throw error
    }

    return await response.json()
  },

  // Get all valuations for the current user
  async getValuations(page = 1, limit = 20, search = '', source = 'all') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort: '-createdAt',
      source,
    })

    if (search) {
      params.append('search', search)
    }

    const response = await fetch(`${API_BASE_URL}/api/valuations?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
    })

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`)
      error.response = response
      throw error
    }

    return await response.json()
  },

  // Get a specific valuation by ID
  async getValuation(id) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
    })

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`)
      error.response = response
      throw error
    }

    return await response.json()
  },

  // Create a new valuation
  async createValuation(valuationData) {
    const response = await fetch(`${API_BASE_URL}/api/valuations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
      body: JSON.stringify(valuationData),
    })

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`)
      error.response = response
      throw error
    }

    return await response.json()
  },

  // Update an existing valuation
  async updateValuation(id, valuationData) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
      body: JSON.stringify(valuationData),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  // Recalculate a valuation after changing exclusions or manual comparable weights
  async recalculateValuation(
    id,
    excludedPropertyIds,
    calculationMethod,
    comparableWeightOverrides = {},
    searchRadiusMeters,
    comparableOverrides = {}
  ) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/${id}/recalculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
      body: JSON.stringify({ excludedPropertyIds, calculationMethod, comparableWeightOverrides, comparableOverrides, searchRadiusMeters }),
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  async updateComparableProperty(propertyId, values) {
    const response = await fetch(`${API_BASE_URL}/api/property/${encodeURIComponent(propertyId)}/comparable-fields`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
      body: JSON.stringify(values),
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  // Generate AI text explicitly from the valuation calculation already stored
  async generateAnalysis(id) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/${id}/analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  // Create or return the current user's active public link
  async shareValuation(id) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/${id}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  // Revoke the current user's public link
  async revokeValuationShare(id) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/${id}/share`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  // Delete a valuation
  async deleteValuation(id) {
    const response = await fetch(`${API_BASE_URL}/api/valuations/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
    })

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`)
      error.response = response
      throw error
    }

    return true
  },
}
